import React from 'react';
import { Section as SectionDI, SkeletonItem as SkeletonItemDI } from '@atlaskit/side-navigation';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntlV2 as useIntlDI } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { MenuItem as MenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { useProject as useProjectDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import { matchUrlParams as matchUrlParamsDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { messages } from './messages.tsx';
import type { IssueAnalysisReportsProps } from './types.tsx';

const useThrowProjectDI = withThrowOnError(useProjectDI);

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default ({
	MenuItem = MenuItemDI,
	Section = SectionDI,
	SkeletonItem = SkeletonItemDI,
	useIntl = useIntlDI,
	useProject = useThrowProjectDI,
	matchUrlParams = matchUrlParamsDI,
}: IssueAnalysisReportsProps) => {
	const { formatMessage } = useIntl();
	const { data: project } = useProject();

	if (project == null) return <SkeletonItem />;

	const baseReportUrl = `/secure/ConfigureReport!default.jspa?selectedProjectId=${project.id}&projectOrFilterId=project-${project.id}&projectOrFilterName=${project.name}`;

	return (
		<Section
			title={formatMessage(
				fg('jira-issue-terminology-refresh-m3')
					? messages.issueAnalysisIssueTermRefresh
					: messages.issueAnalysis,
			)}
		>
			<MenuItem
				analytics={{ itemId: 'average-age' }}
				selectedOn={matchUrlParams(['reportKey'])}
				href={`${baseReportUrl}&reportKey=com.atlassian.jira.plugin.system.reports:averageage-report`}
			>
				{formatMessage(messages.averageAge)}
			</MenuItem>
			<MenuItem
				analytics={{ itemId: 'created-vs-resolved' }}
				selectedOn={matchUrlParams(['reportKey'])}
				href={`${baseReportUrl}&reportKey=com.atlassian.jira.plugin.system.reports:createdvsresolved-report`}
			>
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.createdResolvedIssuesIssueTermRefresh
						: messages.createdResolvedIssues,
				)}
			</MenuItem>
			<MenuItem
				analytics={{ itemId: 'pie' }}
				selectedOn={matchUrlParams(['reportKey'])}
				href={`${baseReportUrl}&reportKey=com.atlassian.jira.plugin.system.reports:pie-report`}
			>
				{formatMessage(messages.pieChart)}
			</MenuItem>
			<MenuItem
				analytics={{ itemId: 'recently-created' }}
				selectedOn={matchUrlParams(['reportKey'])}
				href={`${baseReportUrl}&reportKey=com.atlassian.jira.plugin.system.reports:recentlycreated-report`}
			>
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.recentlyCreatedIssuesIssueTermRefresh
						: messages.recentlyCreatedIssues,
				)}
			</MenuItem>
			<MenuItem
				analytics={{ itemId: 'resolution-time' }}
				selectedOn={matchUrlParams(['reportKey'])}
				href={`${baseReportUrl}&reportKey=com.atlassian.jira.plugin.system.reports:resolutiontime-report`}
			>
				{formatMessage(messages.resolutionTime)}
			</MenuItem>
			<MenuItem
				analytics={{ itemId: 'single-level-group-by' }}
				selectedOn={matchUrlParams(['reportKey'])}
				href={`${baseReportUrl}&reportKey=com.atlassian.jira.plugin.system.reports:singlelevelgroupby`}
			>
				{formatMessage(messages.singleLevelGroup)}
			</MenuItem>
			<MenuItem
				analytics={{ itemId: 'time-since' }}
				selectedOn={matchUrlParams(['reportKey'])}
				href={`${baseReportUrl}&reportKey=com.atlassian.jira.plugin.system.reports:timesince-report`}
			>
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.timeSinceIssuesIssueTermRefresh
						: messages.timeSinceIssues,
				)}
			</MenuItem>
		</Section>
	);
};
